import { useApiQuery } from "./useApi";

/** Clés des quatre consommables (cf. CONTEXT.md § Consommation). */
export type ConsumableKey = 'sms' | 'email' | 'ai' | 'bot';

/**
 * `low` et `exhausted` ne diffèrent pas par l'intensité mais par la nature : le
 * premier annonce, le second constate une interruption de service. D'où deux
 * poids visuels distincts — pastille contre bandeau.
 */
export type ConsumableLevel = 'ok' | 'low' | 'exhausted';

/**
 * Slug de la politique de dépassement : ce que l'établissement perd exactement
 * à zéro. Traduit côté i18n, jamais construit côté backend.
 */
export type ConsumablePolicy =
    | 'sms_all_blocked'
    | 'email_marketing_only'
    | 'ai_except_free'
    | 'bot_stops_replying';

export type Consumable = {
    key: ConsumableKey;
    balance: number;
    threshold: number;
    /** Seuil recommandé, dans la même unité — source : Consumable::defaultThreshold(). */
    default_threshold: number;
    is_unlimited: boolean;
    level: ConsumableLevel;
    /**
     * L'établissement dépense-t-il réellement ce consommable (ADR 0007) ?
     *
     * Gate les surfaces d'ALERTE — bandeau, pastille, e-mail du scan — et elles
     * seules. Les surfaces de CONSULTATION (dropdown de soldes, réglages de
     * seuils) affichent les quatre quoi qu'il arrive : l'utilisateur y est allé
     * chercher l'information, la lui cacher serait la moitié du travail.
     */
    is_in_use: boolean;
    policy: ConsumablePolicy;
};

export type ConsumableMember = {
    facility_id: number;
    name: string;
    consumables: Consumable[];
};

export type ConsumablesResponse = {
    facility_id: number;
    consumables: Consumable[];
    /** Présent uniquement quand l'établissement sélectionné est un groupe. */
    members?: ConsumableMember[];
};

/**
 * Soldes de l'établissement sélectionné (et de ses membres s'il s'agit d'un
 * groupe). L'établissement est résolu côté serveur via le header X-Facility-Id
 * — ne JAMAIS passer de facility_id en query : les panels hors ContextProvider
 * enverraient 0 et se feraient rejeter par le seam tenant.
 */
export function useConsumables() {
    return useApiQuery<ConsumablesResponse>('/consumables', {}, {
        staleTime: 60_000,
        refetchOnWindowFocus: true,
    });
}

